import { useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  IoHomeOutline,
  IoBookOutline,
  IoPeopleOutline,
  IoStatsChartOutline,
  IoCloseOutline,
  IoLogOutOutline,
  IoSettingsOutline,
  IoShieldCheckmarkOutline,
  IoBusinessOutline
} from 'react-icons/io5';
import { useAuth } from '../contexts/AuthContext';
import './Sidebar.css';

const Sidebar = ({ isOpen, onClose }) => {
  const panel = useRef(null);
  useEffect(() => {
    const media = window.matchMedia('(min-width: 1100px)');
    const element = panel.current;
    const sync = () => {
      element.inert = !media.matches && !isOpen;
      if (media.matches && isOpen) onClose();
    };
    sync();
    media.addEventListener('change', sync);
    if (!isOpen || media.matches) return () => media.removeEventListener('change', sync);
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    element.querySelector('button')?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      const items = [...element.querySelectorAll('a[href], button:not([disabled])')];
      const first = items[0], last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    element.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      element.removeEventListener('keydown', onKey);
      media.removeEventListener('change', sync);
      previousFocus?.focus();
    };
  }, [isOpen, onClose]);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  const navigationItems = [
    { id: 'home', label: 'Home', icon: IoHomeOutline, path: '/' },
    { id: 'reading', label: 'Read', icon: IoBookOutline, path: '/reading' },
    { id: 'progress', label: 'My Progress', icon: IoStatsChartOutline, path: '/progress' },
    {
      id: 'church',
      label: 'My Church',
      icon: IoBusinessOutline,
      path: '/church',
      matchPaths: ['/church', '/church-management']
    },
    { id: 'family', label: 'Family', icon: IoPeopleOutline, path: '/family' },
    { id: 'settings', label: 'Settings', icon: IoSettingsOutline, path: '/settings' },
    ...(user?.isAdmin
      ? [{ id: 'admin', label: 'Admin', icon: IoShieldCheckmarkOutline, path: '/admin/dashboard', matchPaths: ['/admin'] }]
      : [])
  ];

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    await logout();
    onClose();
  };

  const isItemActive = (item) => {
    const path = location.pathname;
    const candidates = item.matchPaths || [item.path];
    return candidates.some((candidate) => {
      if (candidate === '/') return path === '/';
      return path === candidate || path.startsWith(`${candidate}/`);
    });
  };

  return (
    <>
      <aside ref={panel} id="app-navigation" aria-label="Main navigation" className={`sidebar ${isOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-title-container">
            <img src="/logo.jpeg" alt="ReadTheWord Logo" className="sidebar-logo" />
            <h2 className="sidebar-title">ReadTheWord</h2>
          </div>
          <button
            className="sidebar-close-button"
            onClick={onClose}
            aria-label="Close menu"
          >
            <IoCloseOutline size={24} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul className="nav-list">
            {navigationItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = isItemActive(item);
              return (
                <li key={item.id} className="nav-item">
                  <button
                    className={`nav-link ${isActive ? 'active' : ''}`}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => handleNavigation(item.path)}
                  >
                    <IconComponent className="nav-icon" size={20} />
                    <span className="nav-label">{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="sidebar-footer">
          <Link className="sidebar-about" to="/about" onClick={onClose}>The 2026 reading</Link>
          <Link className="sidebar-about" to="/privacy" onClick={onClose}>Privacy policy</Link>
          <button
            className="logout-button"
            onClick={handleLogout}
          >
            <IoLogOutOutline className="logout-icon" size={20} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
